import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';


import { Helmet, HelmetProvider } from '@dr.pogodin/react-helmet';

import { environment } from '@anyx/app-core/env';

import { AuthProvider } from '@anyx/common-auth';
import { PermissionProvider } from '@anyx/common-permission';
import { ErrorsRoutePath, HomeRoutePath, OnboardingRoutePath } from '@anyx/common-routing';
import { SelfProvider, useSelfCtx } from '@anyx/common-self';
import { useWorkspaceCtx, WorkspaceProvider } from '@anyx/common-workspace';
import { FirebaseProvider } from '@anyx/external-firebase';
import { AccountDetailProvider } from '@anyx/feature-account';
import { usePolicyDomainLink } from '@anyx/function-policy';
import { useDomainProduct, getAppNameByDomainProduct } from '@anyx/shared-domain-product';
import { ThemeProvider, ThemeUtils } from '@anyx/shared-theme';
import {
  AppLayout,
  ErrorBoundaryMessage,
  LoadWrapper,
  PwaNotificationBanner,
  Toaster,
  ModalsProvider,
  Footer,
  ErrorBoundary,
} from '@anyx/shared-ui';
import {
  GoogleLocationServiceUtils,
  LocationProvider,
  useDocumentHead,
  useEnvironmentStore,
  FetchProvider,
  DatesProvider,
} from '@anyx/shared-utils';

import { ApolloProvider } from './apollo';
import { TimezoneCheck, DomainSwitcher, Navigation, MaintenanceNotifier } from './components';
import { AnyxAuthProvider } from './providers';

/**
 * LoadingGate waits for both Self and Workspace to be ready before rendering children.
 * Auth state is handled by AuthProvider's LoadWrapper - we only care about data readiness here.
 */
const LoadingGate = ({ children }: { children: React.ReactNode }) => {
  const { isSet: selfReady } = useSelfCtx();
  const workspaceReady = useWorkspaceCtx((state) => state.isSet);

  return (
    <LoadWrapper
      loading={!selfReady || !workspaceReady}
      loaderId="session"
      className="flex h-screen"
      message="Loading session..."
    >
      {children}
    </LoadWrapper>
  );
};

export const App = () => {
  const { t } = useTranslation(['core', 'shared']);
  const { product } = useDomainProduct();
  const setAppName = useEnvironmentStore((s) => s.setAppName);
  const appName = useEnvironmentStore((s) => s.appName);
  const { documentTitle } = useDocumentHead({ appName });
  const policyLinks = usePolicyDomainLink({ product, overrideDomain: window.location.origin });

  useEffect(() => {
    GoogleLocationServiceUtils.ENDPOINT = environment.google.locationServiceEndpoint;
  }, []);

  useEffect(() => {
    setAppName(getAppNameByDomainProduct(product));
  }, [product, setAppName]);

  return (
    <HelmetProvider>
      <Helmet>
        <title>{documentTitle}</title>
      </Helmet>
      <ThemeProvider theme={ThemeUtils.getProductTheme(product)}>
        <ErrorBoundary
          fallback={({ error, componentStack, resetError }) => {
            if ((error as Error)?.name === 'ChunkLoadError') {
              window.location.reload();
              return <div />;
            }

            return (
              <ErrorBoundaryMessage
                titleText={t('core.page.error.title')}
                messageText={t('core.page.error.message')}
                btnText={t('shared.button.home', { ns: 'shared' })}
                redirectTo={HomeRoutePath().root().path}
                componentStack={componentStack}
                error={error}
                resetError={resetError}
                showDetails={environment.enableDebug}
              />
            );
          }}
        >
          <AnyxAuthProvider
            clientId={environment.auth0.clientId?.[product]}
            domain={environment.auth0.domain}
            authorizationParams={{
              audience: environment.auth0.audience,
              redirect_uri: window.location.origin,
            }}
            skipRedirectCallbackRegex={[/^\/integrations\/.*$/, /^\/crm\/oauth\/.*$/]}
          >
            <ApolloProvider>
              <FetchProvider baseURL={environment.api.rest}>
                <FirebaseProvider
                  config={environment.firebase.config}
                  remoteConfig={environment.firebase.remoteConfig}
                >
                  <PwaNotificationBanner />
                  <Toaster />
                  <ModalsProvider>
                    <AuthProvider
                      loginOptions={{
                        appState: {
                          returnTo: `${window.location.pathname}${window.location.search}`,
                        },
                        authorizationParams: {
                          product,
                        },
                      }}
                      logoutOptions={{
                        logoutParams: {
                          returnTo: window.location.origin,
                        },
                      }}
                    >
                      {/* SelfProvider and WorkspaceProvider load data in parallel */}
                      <SelfProvider
                        unavailableLink={ErrorsRoutePath().unavailable().path}
                        permissionPath={environment.auth0.permissions}
                      >
                        <WorkspaceProvider
                          onboardingRedirect={OnboardingRoutePath().root().path}
                          children={(workspace) => (
                            <LoadingGate>
                              {/* Everything below only renders when both Self and Workspace are done */}
                              <DatesProvider>
                                <LocationProvider>
                                  <PermissionProvider permissionPath={environment.auth0.permissions}>
                                    <TimezoneCheck />
                                    <AccountDetailProvider accountId={workspace?.accountId}>
                                      <DomainSwitcher />
                                      <MaintenanceNotifier checkInterval={3600000} />
                                      <AppLayout
                                        navigation={<Navigation />}
                                        footer={<Footer {...policyLinks} />}
                                        content={
                                          <Suspense
                                            fallback={
                                              <LoadWrapper
                                                loading={true}
                                                message="This is Route Content loader"
                                                loaderId="route"
                                                className="absolute inset-0 z-20 flex min-h-screen w-full justify-center overflow-hidden"
                                              />
                                            }
                                          >
                                            <Outlet />
                                          </Suspense>
                                        }
                                      />
                                    </AccountDetailProvider>
                                  </PermissionProvider>
                                </LocationProvider>
                              </DatesProvider>
                            </LoadingGate>
                          )}
                        />
                      </SelfProvider>
                    </AuthProvider>
                  </ModalsProvider>
                </FirebaseProvider>
              </FetchProvider>
            </ApolloProvider>
          </AnyxAuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </HelmetProvider>
  );
};
